import { normalizarNumeroUc } from './agrupamentoFaturamento.ts';
import { mesParaIndice } from './analiseTecnicaConsumo.ts';
import { STATUS_LABELS } from './crmVisuals.ts';

/**
 * Cruzamentos da planilha da Análise Cadastral com o que fica fora da
 * negociação: UC do cadastro antigo, datas do ciclo das faturas, mês de entrada
 * no rateio e primeira fatura. Só lógica pura — as buscas ficam no export.
 */

const MESES = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];

/** Índice absoluto (ano*12 + mês-1) → 'MAI/2026', o mesmo rótulo das listas de rateio e das faturas. */
export const rotuloMesAno = (indice: number): string =>
    `${MESES[indice % 12]}/${Math.floor(indice / 12)}`;

/** '2026-05' → índice absoluto. */
export function indiceDoMesIso(mesIso: string | null | undefined): number | null {
    const m = /^(\d{4})-(\d{2})$/.exec(String(mesIso ?? '').trim());
    if (!m) return null;
    const mes = Number(m[2]);
    if (mes < 1 || mes > 12) return null;
    return Number(m[1]) * 12 + mes - 1;
}

// ─── UC do cadastro antigo ─────────────────────────────────────────────

/**
 * "100000000000" foi digitado no lugar da UC em negociação antiga, e existe UC
 * cadastrada com esse número. Casar por ele puxaria UC de outro cliente.
 */
const UC_DE_MENTIRA = /^10*$/;

/**
 * Números de UC do campo livre `negociacoes.numero_uc` ("10036014336, 10036767530"),
 * já normalizados como as colunas *_norm do banco.
 */
export function numerosUcDaNegociacao(texto: string | null | undefined): string[] {
    if (!texto) return [];
    const numeros = String(texto)
        .split(/[,;/\n]|\s+e\s+/i)
        .map(parte => normalizarNumeroUc(parte))
        .filter(n => n.length > 0 && !UC_DE_MENTIRA.test(n));
    return [...new Set(numeros)];
}

export interface UcComNumeros {
    numero_uc_norm?: string | null;
    outro_numero_uc_norm?: string | null;
}

/** Os dois números pelos quais a UC é achada: o atual e o antigo (troca de numeração da Equatorial). */
export function chavesDaUc(uc: UcComNumeros | null | undefined): string[] {
    if (!uc) return [];
    const chaves = [uc.numero_uc_norm, uc.outro_numero_uc_norm]
        .map(v => normalizarNumeroUc(v))
        .filter(Boolean);
    return [...new Set(chaves)];
}

export interface LinhaUcDaNegociacao<T> {
    /** UC cadastrada que casou com o número; null quando o número não tem cadastro. */
    uc: T | null;
    numero: string;
}

/**
 * UCs de uma negociação que não tem nenhuma UC com `negociacao_id` apontando pra
 * ela — as ~360 negociações aprovadas antes do cadastro de UC entrar no fluxo.
 * A UC existe, mas foi importada sem vínculo e muitas vezes já com o número novo
 * da Equatorial, ficando o antigo (o da negociação) em `outro_numero_uc`.
 *
 * Casa só por número, de propósito: por CPF entraria UC de outro contrato do
 * mesmo cliente. Número sem cadastro vira linha só com o número, pra planilha
 * continuar com uma linha por UC.
 */
export function ucsPeloNumeroDaNegociacao<T extends UcComNumeros>(
    numeroUcDaNegociacao: string | null | undefined,
    candidatas: readonly T[],
): LinhaUcDaNegociacao<T>[] {
    const linhas: LinhaUcDaNegociacao<T>[] = [];
    const usadas = new Set<T>();
    for (const numero of numerosUcDaNegociacao(numeroUcDaNegociacao)) {
        const achadas = candidatas.filter(uc => chavesDaUc(uc).includes(numero));
        if (achadas.length === 0) {
            linhas.push({ uc: null, numero });
            continue;
        }
        for (const uc of achadas) {
            if (usadas.has(uc)) continue;
            usadas.add(uc);
            linhas.push({ uc, numero });
        }
    }
    return linhas;
}

// ─── Datas do ciclo ────────────────────────────────────────────────────

/**
 * Dia do mês com dois dígitos ("05"), aceitando o que cada fonte grava: só o dia
 * (análise técnica), data ISO (faturas, histórico manual e alguns registros da
 * análise) ou dd/mm/aaaa. Data-lixo ("0001-01-01") vira vazio.
 */
export function diaDoMes(valor: string | null | undefined): string {
    const s = String(valor ?? '').trim();
    if (!s) return '';
    let dia: number | null = null;
    const iso = /^(\d{4})-\d{2}-(\d{2})/.exec(s);
    const br = /^(\d{1,2})\/\d{1,2}\/(\d{2}|\d{4})$/.exec(s);
    if (iso) dia = Number(iso[1]) >= 1900 ? Number(iso[2]) : null;
    else if (br) dia = Number(br[1]);
    else if (/^\d{1,2}$/.test(s)) dia = Number(s);
    return dia !== null && dia >= 1 && dia <= 31 ? String(dia).padStart(2, '0') : '';
}

/** Primeiro valor que vira dia válido, na ordem de prioridade das fontes. */
export const primeiroDia = (...fontes: (string | null | undefined)[]): string => {
    for (const fonte of fontes) {
        const dia = diaDoMes(fonte);
        if (dia) return dia;
    }
    return '';
};

// ─── Faturas ───────────────────────────────────────────────────────────

/** Recorte de `LinhaDatasFatura` (RPC relatorio_datas_faturas) que a planilha usa. */
export interface FaturaComDatas {
    uc: string;
    mes_iso: string;
    leitura_atual: string | null;
    apresentacao: string | null;
    vencimento: string | null;
}

type CampoData = 'leitura_atual' | 'apresentacao' | 'vencimento';
const CAMPOS_DATA: CampoData[] = ['leitura_atual', 'apresentacao', 'vencimento'];

interface ResumoFaturasDaUc {
    primeiroMes: number;
    /** Data mais recente de cada campo e o mês da fatura de onde ela veio. */
    datas: Partial<Record<CampoData, { mes: number; valor: string }>>;
}

export type IndiceFaturas = Map<string, ResumoFaturasDaUc>;

/**
 * Por UC (normalizada): mês da primeira fatura e a data mais recente de leitura,
 * apresentação e vencimento. Mês depois de `ultimoMesValido` é erro de extração
 * (tem fatura "SET/2027") e não pode virar primeira fatura nem dia do ciclo.
 */
export function indexarFaturas(faturas: readonly FaturaComDatas[], ultimoMesValido: number): IndiceFaturas {
    const indice: IndiceFaturas = new Map();
    for (const f of faturas) {
        const uc = normalizarNumeroUc(f.uc);
        const mes = indiceDoMesIso(f.mes_iso);
        if (!uc || mes === null || mes > ultimoMesValido) continue;
        let resumo = indice.get(uc);
        if (!resumo) {
            resumo = { primeiroMes: mes, datas: {} };
            indice.set(uc, resumo);
        }
        if (mes < resumo.primeiroMes) resumo.primeiroMes = mes;
        for (const campo of CAMPOS_DATA) {
            const valor = f[campo];
            if (!valor) continue;
            const atual = resumo.datas[campo];
            if (!atual || mes > atual.mes) resumo.datas[campo] = { mes, valor };
        }
    }
    return indice;
}

/** Primeira fatura entre todos os números da UC ('MAI/2026'), ou vazio. */
export function primeiraFatura(indice: IndiceFaturas, chaves: readonly string[]): string {
    let primeiro: number | null = null;
    for (const chave of chaves) {
        const resumo = indice.get(chave);
        if (resumo && (primeiro === null || resumo.primeiroMes < primeiro)) primeiro = resumo.primeiroMes;
    }
    return primeiro === null ? '' : rotuloMesAno(primeiro);
}

/** Data mais recente do campo entre todos os números da UC. */
export function dataMaisRecente(indice: IndiceFaturas, chaves: readonly string[], campo: CampoData): string | null {
    let melhor: { mes: number; valor: string } | null = null;
    for (const chave of chaves) {
        const data = indice.get(chave)?.datas[campo];
        if (data && (!melhor || data.mes > melhor.mes)) melhor = data;
    }
    return melhor?.valor ?? null;
}

// ─── Histórico de consumo das faturas ──────────────────────────────────

/**
 * Número como vem da extração e da análise: "1.234,56", "357,00", "173.00" ou
 * número. Ponto com três casas depois é milhar ("1.234"), não decimal.
 */
export function lerNumeroBr(valor: string | number | null | undefined): number {
    if (valor === null || valor === undefined || valor === '') return 0;
    let s = String(valor).trim();
    if (s.endsWith('.00')) s = s.slice(0, -3);
    if (s.includes(',')) return parseFloat(s.replace(/\./g, '').replace(',', '.')) || 0;
    const pontos = (s.match(/\./g) || []).length;
    if (pontos > 1) return parseFloat(s.replace(/\./g, '')) || 0;
    if (pontos === 1 && s.split('.')[1].length === 3) return parseFloat(s.replace('.', '')) || 0;
    return parseFloat(s) || 0;
}

/** Extração de fatura (`extracoes_faturas`) — só o que serve pra escolher a mais recente. */
export interface ExtracaoResumo {
    id: number;
    uc_codigo_norm: string | null;
    /** '2026-10' */
    mes_referencia: string | null;
    status: string | null;
}

/**
 * Extração mais recente de cada UC (normalizada): maior mês de referência e,
 * no mesmo mês, a 'ok' antes da 'revisar'. Extração que falhou não entra.
 */
export function extracaoMaisRecentePorUc(extracoes: readonly ExtracaoResumo[]): Map<string, number> {
    const melhor = new Map<string, ExtracaoResumo>();
    for (const e of extracoes) {
        const uc = normalizarNumeroUc(e.uc_codigo_norm);
        if (!uc || e.status === 'falhou' || indiceDoMesIso(e.mes_referencia) === null) continue;
        const atual = melhor.get(uc);
        const ganha = !atual
            || String(e.mes_referencia) > String(atual.mes_referencia)
            || (e.mes_referencia === atual.mes_referencia && e.status === 'ok' && atual.status !== 'ok');
        if (ganha) melhor.set(uc, e);
    }
    return new Map([...melhor].map(([uc, e]) => [uc, e.id]));
}

export interface MesConsumo { indice: number; consumo: number }

/**
 * Quadro de histórico impresso na fatura ([{ mes_ano: 'OUT/26', consumo_faturado: '357,00' }])
 * → 12 meses, do mais novo pro mais velho. Mesmas regras da análise técnica:
 * consumo zerado conta como sem registro, mês depois de `ultimoMesValido` é lixo
 * de extração, mês repetido fica o primeiro (o mais recente).
 */
export function mesesDoHistoricoDaFatura(
    historico: readonly { mes_ano?: string | null; consumo_faturado?: string | number | null }[] | null | undefined,
    ultimoMesValido: number,
): MesConsumo[] {
    const meses: MesConsumo[] = [];
    const vistos = new Set<number>();
    for (const h of historico || []) {
        if (meses.length === 12) break;
        const indice = mesParaIndice(h?.mes_ano ?? '');
        const consumo = lerNumeroBr(h?.consumo_faturado);
        if (indice === null || indice > ultimoMesValido || !(consumo > 0) || vistos.has(indice)) continue;
        vistos.add(indice);
        meses.push({ indice, consumo });
    }
    return meses;
}

// ─── Entrada no rateio ─────────────────────────────────────────────────

/** Item das listas mensais de rateio (Gestão › Rateio Antigo), com o mês da lista. */
export interface ItemListaRateio {
    uc: string | null;
    outro_numero_uc: string | null;
    mes_referencia: string | null;
}

export interface IndiceRateio {
    /** UC (normalizada) → primeira lista em que apareceu. */
    primeiraLista: Map<string, number>;
    /** Lista mais antiga que existe: quem já está nela entrou nesse mês ou antes. */
    listaMaisAntiga: number | null;
}

export function indexarListasRateio(itens: readonly ItemListaRateio[]): IndiceRateio {
    const primeiraLista = new Map<string, number>();
    let listaMaisAntiga: number | null = null;
    for (const item of itens) {
        const mes = mesParaIndice(item.mes_referencia ?? '');
        if (mes === null) continue;
        if (listaMaisAntiga === null || mes < listaMaisAntiga) listaMaisAntiga = mes;
        for (const numero of [item.uc, item.outro_numero_uc]) {
            const chave = normalizarNumeroUc(numero);
            if (!chave) continue;
            const atual = primeiraLista.get(chave);
            if (atual === undefined || mes < atual) primeiraLista.set(chave, mes);
        }
    }
    return { primeiraLista, listaMaisAntiga };
}

/**
 * Mês em que a UC entrou no rateio ('MAI/2026'). Vem da primeira lista mensal em
 * que ela aparece; quem já está na lista mais antiga sai "MAR/2026 ou antes",
 * porque antes dela não há registro. Sem lista, cai no vínculo com a UG feito no
 * Simulador de Rateio (`rateio_vinculado_em`).
 */
export function inclusaoNoRateio(
    indice: IndiceRateio,
    chaves: readonly string[],
    vinculadoEm?: string | null,
): string {
    let primeiro: number | null = null;
    for (const chave of chaves) {
        const mes = indice.primeiraLista.get(chave);
        if (mes !== undefined && (primeiro === null || mes < primeiro)) primeiro = mes;
    }
    if (primeiro !== null) {
        return primeiro === indice.listaMaisAntiga
            ? `${rotuloMesAno(primeiro)} ou antes`
            : rotuloMesAno(primeiro);
    }
    const vinculo = /^(\d{4})-(\d{2})/.exec(String(vinculadoEm ?? ''));
    return vinculo ? rotuloMesAno(Number(vinculo[1]) * 12 + Number(vinculo[2]) - 1) : '';
}

// ─── Mesma leitura do CRM ──────────────────────────────────────────────
// A tela e a planilha da Análise Cadastral precisam dizer o mesmo que o Kanban
// do CRM. Antes, 20 negociações paradas em "Termo em análise" no Kanban saíam
// como "Reprovado - Pendência Documental" aqui, e a data da análise era a da
// última edição (updated_at), não a da decisão que o Kanban e a Folha usam.

/**
 * Rótulo da decisão. Pendência Documental e Exigir Garantia só viram
 * "Reprovado - ..." quando a negociação foi reprovada de fato; enquanto ela
 * está em análise é pendência — é assim que o Kanban mostra ("Termo em
 * análise") e que o card "Reanálise" da própria tela conta.
 */
export function rotuloDecisaoAnalise(decisao?: string | null, status?: string | null): string {
    if (decisao === 'Pendência Documental' || decisao === 'Exigir Garantia') {
        return status === 'termo_reprovado' ? `Reprovado - ${decisao}` : decisao;
    }
    if (decisao) return decisao;
    switch (status) {
        case 'termo_aprovado': return 'Aprovado';
        case 'termo_reprovado': return 'Reprovado';
        case 'termo_analise': return 'Em Análise';
        case 'termo_assinado': return 'Pendente';
        default: return status || '-';
    }
}

/** Data da decisão da análise — a mesma que o Kanban e a Folha usam para o mês da aprovação. */
export function dataDaDecisao(
    analise: { decisao_final_at?: string | null; updated_at?: string | null; created_at?: string | null } | null | undefined,
): string | null {
    return analise?.decisao_final_at || analise?.updated_at || analise?.created_at || null;
}

/** Onde a negociação está no Kanban: "Pós-Venda › Onboarding · Termo aprovado". */
export function situacaoNoCrm(
    funil: string | null | undefined,
    etapa: string | null | undefined,
    status: string | null | undefined,
): string {
    const onde = `${funil || 'Sem funil'} › ${etapa || 'Sem etapa'}`;
    return status ? `${onde} · ${STATUS_LABELS[status] || status}` : onde;
}

// ─── Formatação ────────────────────────────────────────────────────────

/** Desconto gravado como fração (0.2) → "20%". Zero é resposta e sai "0%"; vazio é vazio. */
export function percentualDeFracao(valor: number | string | null | undefined): string {
    if (valor === null || valor === undefined || valor === '') return '';
    const n = Number(valor);
    if (!Number.isFinite(n)) return '';
    return `${(n * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
}

/** 'YYYY-MM-DD' (coluna date) → 'DD/MM/AAAA' sem passar por Date, que joga pro dia anterior no fuso de Brasília. */
export function dataIsoParaBr(valor: string | null | undefined): string {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(valor ?? ''));
    return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
}
